import { useState, FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import rlsvoteApi from './rlsvoteApi'

type Stats = { stato: string; partecipanti: number; schede: number }
type Votante = { id: string; nome: string; cognome: string; dichiarato_il: string | null; documento_disponibile: boolean }

const EXT_BY_CONTENT_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/pdf': 'pdf',
}

function scaricaBlob(data: Blob, filename: string) {
  const url = window.URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

export default function AdminDashboard() {
  const { id } = useParams<{ id: string }>()
  const [token, setToken] = useState<string | null>(null)
  const [pin, setPin] = useState('')
  const [loginErrore, setLoginErrore] = useState<string | null>(null)

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined

  const { data: stats, refetch } = useQuery<Stats>({
    queryKey: ['rlsvote-stats', id, token],
    queryFn: async () => (await rlsvoteApi.get(`/elezioni/${id}/stats`, { headers: authHeaders })).data,
    enabled: !!token,
    refetchInterval: 5000,
  })

  const { data: votanti } = useQuery<Votante[]>({
    queryKey: ['rlsvote-votanti', id, token],
    queryFn: async () => (await rlsvoteApi.get(`/elezioni/${id}/report-votanti`, { headers: authHeaders })).data,
    enabled: !!token && stats?.stato === 'chiusa',
  })

  async function handleLogin(e: FormEvent) {
    e.preventDefault()
    setLoginErrore(null)
    try {
      const res = await rlsvoteApi.post(`/elezioni/${id}/login`, { pin })
      setToken(res.data.access_token)
    } catch {
      setLoginErrore('PIN non valido')
    }
  }

  async function apri() {
    try {
      await rlsvoteApi.post(`/elezioni/${id}/apri`, {}, { headers: authHeaders })
      toast.success('Elezione aperta')
      refetch()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore')
    }
  }

  async function chiudi() {
    try {
      await rlsvoteApi.post(`/elezioni/${id}/chiudi`, {}, { headers: authHeaders })
      toast.success('Elezione chiusa')
      refetch()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore')
    }
  }

  async function scaricaReportAnonimo() {
    const res = await rlsvoteApi.get(`/elezioni/${id}/report-anonimo`, { headers: authHeaders, responseType: 'blob' })
    scaricaBlob(res.data, `risultato_${id}.json`)
  }

  async function scaricaVerbale() {
    const res = await rlsvoteApi.get(`/elezioni/${id}/verbale`, { headers: authHeaders, responseType: 'blob' })
    scaricaBlob(res.data, `verbale_${id}.pdf`)
  }

  async function scaricaDocumento(partecipanteId: string, cognome: string, nome: string) {
    try {
      const res = await rlsvoteApi.get(`/elezioni/${id}/documenti/${partecipanteId}`, { headers: authHeaders, responseType: 'blob' })
      const ext = EXT_BY_CONTENT_TYPE[res.data.type] || 'bin'
      scaricaBlob(res.data, `documento_${cognome}_${nome}.${ext}`)
    } catch {
      toast.error('Documento non disponibile')
    }
  }

  async function scaricaZip() {
    try {
      const res = await rlsvoteApi.get(`/elezioni/${id}/documenti/zip`, { headers: authHeaders, responseType: 'blob' })
      scaricaBlob(res.data, `documenti_identita_${id}.zip`)
    } catch {
      toast.error('Errore durante il download dello ZIP')
    }
  }

  async function eliminaDocumenti() {
    try {
      await rlsvoteApi.delete(`/elezioni/${id}/documenti`, { headers: authHeaders })
      toast.success('Documenti eliminati')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore')
    }
  }

  if (!token) {
    return (
      <div className="max-w-sm mx-auto p-6">
        <h1 className="text-xl font-bold mb-4">Accesso commissione elettorale</h1>
        <form onSubmit={handleLogin} className="space-y-3">
          <input
            className="input w-full"
            type="password"
            placeholder="PIN elezione"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            required
          />
          {loginErrore && <p className="text-red-600 text-sm">{loginErrore}</p>}
          <button type="submit" className="btn-primary w-full">Accedi</button>
        </form>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <h1 className="text-xl font-bold">Dashboard elezione RLS</h1>

      <div className="card p-4">
        <p>Stato: <strong>{stats?.stato ?? '—'}</strong></p>
        <p>Partecipanti registrati: <strong>{stats?.partecipanti ?? 0}</strong></p>
        <p>Schede votate: <strong>{stats?.schede ?? 0}</strong></p>
        <div className="flex gap-2 mt-4">
          <button className="btn-secondary" onClick={apri} disabled={stats?.stato !== 'setup'}>Apri elezione</button>
          <button className="btn-secondary" onClick={chiudi} disabled={stats?.stato !== 'aperta'}>Chiudi elezione</button>
        </div>
      </div>

      <div className="card p-4 space-y-2">
        <p className="font-medium">Report e verbale (disponibili a elezione chiusa)</p>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-secondary" onClick={scaricaReportAnonimo} disabled={stats?.stato !== 'chiusa'}>Report anonimo</button>
          <button className="btn-secondary" onClick={scaricaVerbale} disabled={stats?.stato !== 'chiusa'}>Verbale PDF</button>
          <button className="btn-secondary" onClick={scaricaZip} disabled={stats?.stato !== 'chiusa'}>Scarica tutti i documenti (ZIP)</button>
          <button className="btn-ghost text-red-600" onClick={eliminaDocumenti} disabled={stats?.stato !== 'chiusa'}>
            Elimina documenti identità
          </button>
        </div>
      </div>

      {stats?.stato === 'chiusa' && (
        <div className="card p-4">
          <p className="font-medium mb-3">Votanti</p>
          {!votanti || votanti.length === 0 ? (
            <p className="text-sm text-gray-500">Nessun votante registrato.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-1 pr-2">Nome</th>
                  <th className="py-1 pr-2">Cognome</th>
                  <th className="py-1 pr-2">Dichiarato il</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {votanti.map((v) => (
                  <tr key={v.id} className="border-b last:border-0">
                    <td className="py-1 pr-2">{v.nome}</td>
                    <td className="py-1 pr-2">{v.cognome}</td>
                    <td className="py-1 pr-2">{v.dichiarato_il ? new Date(v.dichiarato_il).toLocaleString('it-IT') : '—'}</td>
                    <td className="py-1">
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '2px 8px' }}
                        onClick={() => scaricaDocumento(v.id, v.cognome, v.nome)}
                        disabled={!v.documento_disponibile}
                      >
                        Scarica documento
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
